import { useCallback } from 'react'
import type { PortInfoFormData, PageData } from '../types'

interface FormHandlers {
  onChangeHandler: <K extends keyof PortInfoFormData>(args: {
    key: K
    value: PortInfoFormData[K]
  }) => void
  handleNumberChange: (key: keyof PortInfoFormData, value: string) => void
  handleVesselChange: (
    vesselName: string | undefined,
    imoNumber: string | undefined,
  ) => void
}

export function useFormHandlers(
  formData: PortInfoFormData,
  updateFormData: PageData['updateFormData'],
): FormHandlers {
  const onChangeHandler = useCallback(
    <K extends keyof PortInfoFormData>({
      key,
      value,
    }: {
      key: K
      value: PortInfoFormData[K]
    }) => {
      if (formData[key] !== value) {
        updateFormData({ ...formData, [key]: value })
      }
    },
    [formData, updateFormData],
  )

  const handleNumberChange = useCallback(
    (key: keyof PortInfoFormData, value: string) => {
      const parsed = parseFloat(value)
      const num =
        value === '' ? undefined : Number.isNaN(parsed) ? undefined : parsed
      onChangeHandler({ key, value: num as PortInfoFormData[typeof key] })
    },
    [onChangeHandler],
  )

  const handleVesselChange = useCallback(
    (vesselName: string | undefined, imoNumber: string | undefined) => {
      updateFormData({
        ...formData,
        vesselName,
        imoNumber,
      })
    },
    [formData, updateFormData],
  )

  return {
    onChangeHandler,
    handleNumberChange,
    handleVesselChange,
  }
}
